import { Logo } from "@mesketch/svg";
import { useEffect } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Link, Outlet, useLocation } from "react-router";
import { useIdentity } from "../data/identity.js";
import { applyUpdate } from "../pwa-state.js";

// Kostra stránky (REQ-017): hlavička s logem, odkazem Moje já (REQ-033, jen když instance identitu
// má) a přepínačem jazyka, patička s podpisem SEL Institute a odkazy na podmínky. Přepínač otevře
// tutéž stránku v druhém jazyce.

export function otherLocalePath(pathname: string, locale: string): string {
  const other = locale === "cs" ? "en" : "cs";
  const rest = pathname.replace(/^\/(cs|en)(?=\/|$)/, "");
  return `/${other}${rest === "" ? "/" : rest}`;
}

export function Shell() {
  const intl = useIntl();
  const { pathname, search } = useLocation();
  const lang = intl.locale;
  const identity = useIdentity();
  useEffect(() => applyUpdate(pathname), [pathname]);
  return (
    <div className="page">
      <header className="shell header">
        <Link to={`/${lang}/`} className="header__logo" title={intl.formatMessage({ id: "nav.home" })}>
          <Logo label={intl.formatMessage({ id: "app.name" })} />
        </Link>
        <nav className="header__nav">
          {identity.kind === "anon" || identity.kind === "in" ? (
            <Link to={`/${lang}/me`} className="header__me" data-testid="nav-me">
              <FormattedMessage id="nav.me" />
            </Link>
          ) : null}
          <Link to={otherLocalePath(pathname, lang) + search} className="lang-switch" hrefLang={lang === "cs" ? "en" : "cs"} aria-label={intl.formatMessage({ id: "lang.switch.aria" })}>
            <FormattedMessage id="lang.switch.other" />
          </Link>
        </nav>
      </header>
      <main className="shell main">
        <Outlet />
      </main>
      <footer className="shell footer">
        <span className="footer__by">
          <FormattedMessage id="footer.by" />
        </span>
        <Link to={`/${lang}/terms`}>
          <FormattedMessage id="footer.terms" />
        </Link>
        <Link to={`/${lang}/privacy`}>
          <FormattedMessage id="footer.privacy" />
        </Link>
      </footer>
    </div>
  );
}

/** Pruh náhledu (REQ-031): viditelný na každé obrazovce sezení `preview`. */
export function PreviewBar() {
  return (
    <div className="preview-bar" role="note">
      <strong>
        <FormattedMessage id="preview.banner" />
      </strong>{" "}
      · <FormattedMessage id="preview.note" />
    </div>
  );
}

/** Stránka, která se nemá indexovat (náhled, odkaz na výsledek, sezení, REQ-024, REQ-031). */
export function useNoIndex(): void {
  useEffect(() => {
    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = "noindex, nofollow";
    document.head.append(meta);
    return () => meta.remove();
  }, []);
}

export function useTitle(title: string | undefined): void {
  useEffect(() => {
    document.title = title === undefined || title === "" ? "mesketch" : `${title} · mesketch`;
  }, [title]);
}

/** Titulek úvodní stránky stejný jako v předrenderovaném HTML (CR-025 §1, `prerender.mjs`). */
export function useHomeTitle(): void {
  const intl = useIntl();
  const seo = intl.formatMessage({ id: "home.seoTitle" });
  useEffect(() => {
    document.title = `mesketch · ${seo}`;
  }, [seo]);
}

export function NotFound() {
  return (
    <div className="stack">
      <h1>
        <FormattedMessage id="notFound.title" />
      </h1>
      <p className="muted">
        <FormattedMessage id="notFound.body" />
      </p>
    </div>
  );
}

export function ErrorBox({ offline, onRetry }: { offline?: boolean; onRetry?: () => void }) {
  return (
    <div className="notice notice--quiet stack" role="alert">
      <p>
        <FormattedMessage id={offline === true ? "error.offline" : "error.generic"} />
      </p>
      {onRetry === undefined ? null : (
        <button type="button" className="button button--quiet" onClick={onRetry}>
          <FormattedMessage id="action.retry" />
        </button>
      )}
    </div>
  );
}
